import { useCallback, useEffect, useState } from 'react';
import { ErrorScreen } from 'app-pages/pages/appointments/status-screens/BookingStatusScreens';
import { Slot } from 'components/_temp_ui_common/api/getAvailability';
import {
  Appointment,
  AppointmentStatus,
} from 'components/_temp_ui_common/Appointments/types';
import { CalendarProvider } from 'components/_temp_ui_common/Calendar/CalendarProvider';
import { useConsult } from 'contexts/ConsultContext';
import { useCurrentUser } from 'contexts/CurrentUserContext';
import axiosInstance from 'helpers/axiosInstance';

import { CalendarConfirmed } from '../../../components/CalendarConfirmed';
import { ReviewAndConfirmSelectedSlot } from './ReviewAndConfirmSelectedSlot';
import { SchedulingCalendar } from './SchedulingCalendar';

export interface AppointmentSchedulerProps {
  appointment: Appointment;
  existingAppointments: Appointment[];
  onSchedulingComplete: () => void;
}

export const AppointmentScheduler = ({
  appointment,
  existingAppointments,
  onSchedulingComplete,
}: AppointmentSchedulerProps) => {
  const consultState = useConsult();

  const {
    state: { account },
  } = useCurrentUser();
  const [selectedSlot, setSelectedSlot] = useState<Slot>();
  const [schedulingFailed, setSchedulingFailed] = useState(false);

  const [confirmed, setConfirmed] = useState(false);

  const onSchedulingFailure = useCallback(() => {
    setSchedulingFailed(true);
  }, []);

  const onSelectSlot = useCallback((slot: Slot) => {
    setSelectedSlot(slot);
  }, []);

  const resetToInitialState = useCallback(() => {
    setSchedulingFailed(false);
    setConfirmed(false);
    setSelectedSlot(undefined);
  }, []);

  const onSchedulingSuccessful = useCallback(() => {
    resetToInitialState();
    onSchedulingComplete();
  }, [resetToInitialState, onSchedulingComplete]);

  useEffect(() => {
    resetToInitialState();
  }, [appointment.id, resetToInitialState]);

  if (!account || !consultState?.consult) return null;

  const member = consultState.consult.patient;

  if (schedulingFailed) {
    return <ErrorScreen onTryAgain={resetToInitialState} />;
  }

  let content: JSX.Element | undefined = undefined;

  if (confirmed && selectedSlot) {
    // Step 3: HOORAY! Scheduling is confirmed
    const buttonLabel = existingAppointments.some(
      a => a.status === AppointmentStatus.Unscheduled,
    )
      ? 'Next Appointment'
      : 'Done';
    content = (
      <CalendarConfirmed
        slot={selectedSlot}
        appointment={appointment}
        member={consultState.consult.patient}
        buttonLabel={buttonLabel}
        onDone={onSchedulingSuccessful}
      />
    );
  } else if (selectedSlot) {
    // Step 2: Review and confirm selected time slot
    content = (
      <ReviewAndConfirmSelectedSlot
        appointment={appointment}
        member={member}
        onCancel={resetToInitialState}
        onConfirm={() => setConfirmed(true)}
        onError={onSchedulingFailure}
        selectedSlot={selectedSlot}
      />
    );
  } else {
    // Step 1: Calendar with time slots
    content = (
      <SchedulingCalendar
        appointment={appointment}
        member={member}
        onSelectSlot={onSelectSlot}
      />
    );
  }

  return (
    <CalendarProvider
      appointment={appointment}
      axiosInstance={axiosInstance}
      blockedDays={3}
      defaultTimeZone={account.preferredTimeZone}
      existingAppointments={existingAppointments}
    >
      {content}
    </CalendarProvider>
  );
};
